// Pure helpers for the Options Flow payload — no React, no I/O, so they're
// unit-testable in isolation. This is where the contract's robustness rules
// live: the `_`-prefix / no-`viz` skip rule, the `_meta` fallback, USD
// formatting, and the nullable guards.

import {
  FlowMeta,
  FlowPayload,
  FlowReport,
  FlowReportMeta,
  TokenInfo,
  VenueComparisonReport,
  VenueComparisonWindow,
  VenueDayStats,
  VenueInfo,
  WindowCoverageCell,
} from './types';

// A payload value is a renderable REPORT iff its key does not start with `_`
// AND the value carries a `viz` string. Everything else (top-level `_meta`,
// any future `_`-prefixed metadata, or a malformed entry) is skipped by the
// dispatch loop rather than rendered as a card.
export const isReportEntry = (key: string, value: unknown): value is FlowReport => {
  if (key.startsWith('_')) return false;
  if (!value || typeof value !== 'object') return false;
  return typeof (value as { viz?: unknown }).viz === 'string';
};

// The payload's report entries, in insertion order, as [name, report] pairs.
// Metadata keys are filtered out here so callers never see them.
export const listReports = (payload: FlowPayload | null): [string, FlowReport][] => {
  if (!payload) return [];
  return Object.entries(payload).filter(([key, value]) => isReportEntry(key, value)) as [
    string,
    FlowReport,
  ][];
};

// Header meta: prefer the payload-wide `_meta`; until the backend emits it,
// fall back to the first report's own `meta`. Returns null only for an empty
// payload (no `_meta` and no reports) so the header can render a resting state.
export const resolveHeaderMeta = (
  payload: FlowPayload | null,
): {
  generated_utc: string;
  currency: string;
  venue: string;
  cadence_sec: number | null;
} | null => {
  if (!payload) return null;

  const meta = payload._meta as FlowMeta | undefined;
  if (meta) {
    return {
      generated_utc: meta.generated_utc,
      currency: meta.currency,
      venue: meta.venue,
      cadence_sec: meta.cadence_sec,
    };
  }

  const firstReport = listReports(payload)[0]?.[1];
  if (!firstReport) return null;

  const m = firstReport.meta;
  return {
    generated_utc: m.generated_utc,
    currency: m.currency,
    venue: m.venue,
    // No payload-wide cadence without `_meta` — staleness stays dormant.
    cadence_sec: null,
  };
};

// The window/trade-count strip also comes from the first report's meta (the
// PoC reads it off `net_flow` specifically; we take the first report so the
// header still populates if net_flow is absent).
export const resolveWindowMeta = (payload: FlowPayload | null): FlowReportMeta | null => {
  return listReports(payload)[0]?.[1]?.meta ?? null;
};

// Staleness: LIVE drops when now - generated_utc exceeds 2x the cadence. With
// no cadence (historic fixture, no `_meta`) the rule is dormant and never
// false-alarms — exactly the brief's requirement while data is historic.
export const isFeedStale = (
  generatedUtc: string,
  cadenceSec: number | null,
  now: number = Date.now(),
): boolean => {
  if (cadenceSec == null || cadenceSec <= 0) return false;
  const generatedMs = new Date(generatedUtc).getTime();
  if (Number.isNaN(generatedMs)) return false;
  return now - generatedMs > 2 * cadenceSec * 1000;
};

// USD formatting matching the PoC: 2dp for >= 1tn / >= 1bn / >= 1m, 0dp for
// >= 1k, whole dollars below. Sign prefix preserved (money is server-rounded —
// we format, we never re-round in a way that could flip a sign).
export const fmtUsd = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}tn`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}bn`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}m`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

// Round to `dp` decimals and drop trailing zeros — Number(toFixed) collapses
// "0.630" -> 0.63 and "1.20" -> 1.2. Lenient by design: an already-clean input
// (0.629, 1.18) round-trips unchanged; it only bites if the server slips an
// unrounded value through (1.1834743 -> 1.18).
const roundTrim = (value: number, dp: number): string => String(Number(value.toFixed(dp)));

// Put/call ratio: the contract passes it "as given"; we defensively cap at 3dp
// in case an unrounded value slips through. Em-dash when null.
export const fmtRatio = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return roundTrim(value, 3);
};

// Percentage: the contract passes the number already scaled (1.18 -> "1.18%").
// Defensive 2dp cap so a stray unrounded value doesn't render as "1.1834743%".
export const fmtPercent = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${roundTrim(value, 2)}%`;
};

// Trade counts use locale grouping (98,577).
export const fmtCount = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US');
};

// A strike as a compact `65k` label (chain axis). Non-multiples keep one dp
// (e.g. 64500 -> "64.5k") rather than rounding away a half-strike.
export const fmtStrikeK = (strike: number): string => {
  const k = strike / 1000;
  return Number.isInteger(k) ? `${k}k` : `${k.toFixed(1)}k`;
};

// The currency unit for a report's notional column. Prefer the explicit
// `meta.notional_unit`; the live feed omits it on net_flow/top_flow, so fall
// back to `meta.currency` (reliably present + correct per token — SOL for SOL).
// The final 'BTC' is a belt-and-suspenders backstop only; both fields are
// strings on the feed, so a non-BTC token never mislabels as BTC again.
export const resolveNotionalUnit = (meta: FlowReportMeta): string =>
  meta.notional_unit ?? meta.currency ?? 'BTC';

// Notional (underlying amount transacted), with the currency unit resolved via
// `resolveNotionalUnit` (e.g. "BTC" / "ETH" / "SOL") — never hardcoded, so the
// ETH card reads "… ETH". 2dp magnitude suffix for >= 1m (m / bn / tn — matching
// `fmtUsd`, so billions read "418.53bn BTC" not "418529.30m BTC"), else a full
// locale-grouped integer ("4,037 BTC"). Sign preserved.
export const fmtNotional = (value: number | null | undefined, unit = 'BTC'): string => {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(2)}tn ${unit}`;
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}bn ${unit}`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}m ${unit}`;
  // Full locale-grouped integer below 1m (e.g. "4,037 BTC").
  return `${sign}${Math.round(abs).toLocaleString('en-US')} ${unit}`;
};

// Expiry date label, e.g. "2026-07-31" -> "31 JUL 26" (chain chips + by_expiry).
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export const fmtExpiryChipDate = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
};

// Data-freshness stamp for the AS-OF badge: a full ISO timestamp ->
// "2026-07-22 21:35 UTC" (minutes precision, UTC). A bare date (no time
// component) passes through as the date alone; unparseable input passes through
// verbatim so the badge never renders "Invalid Date".
export const fmtAsOf = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  // Date-only input (e.g. "2026-07-22") parses to 00:00 UTC — but so does a real
  // midnight stamp. Only add the clock when the source string carries a time.
  if (!/\d{2}:\d{2}/.test(iso)) return date;
  const time = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  return `${date} ${time} UTC`;
};

// Clock-granular freshness stamp for the header/selector — "14:37 UTC" from the
// snapshot's `meta.generated_utc`. The 5-min poll refreshes the current hour's
// bar, so the actual minute (NOT a top-of-hour floor) is what makes freshness
// legible. Null for unparseable/absent input so the caller can omit the stamp.
export const fmtAsOfTime = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
};

// Global time-window selector. The feed serves ONE bundled payload carrying
// every window's envelope; switching windows is client-side (no re-request).
export type WindowKey = '24h' | '7d' | '30d' | 'mtd' | 'qtd' | 'ytd';

export const WINDOW_OPTIONS: { key: WindowKey; label: string }[] = [
  { key: '24h', label: '24H' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: 'mtd', label: 'MTD' },
  { key: 'qtd', label: 'QTD' },
  { key: 'ytd', label: 'YTD' },
];

const WINDOW_KEYS = WINDOW_OPTIONS.map((o) => o.key);

export const isWindowKey = (v: unknown): v is WindowKey => WINDOW_KEYS.includes(v as WindowKey);

export const windowLabel = (key: WindowKey): string =>
  WINDOW_OPTIONS.find((o) => o.key === key)?.label ?? String(key).toUpperCase();

// The bundled feed shape: `default_window` + `available_windows` alongside a
// `windows` map keyed by WindowKey, each value a full 12-report envelope (the
// same shape a single-window payload has today).
export interface WindowBundle {
  // Windows the feed actually carries (listed AND present in the map), in the
  // canonical selector order.
  availableWindows: WindowKey[];
  // Initial selection (`default_window`, clamped to an available window).
  defaultWindow: WindowKey;
  // Per-window envelopes; index with the active WindowKey to get what renders.
  windows: Partial<Record<WindowKey, FlowPayload>>;
  // Sparse-window coverage, keyed by WindowKey — present only for sparse windows
  // (dense tokens / older payloads resolve to an empty map). The window-selector
  // reads this to badge the affected toggles; everything else ignores it.
  windowCoverage: WindowCoverageMap;
}

// Per-window coverage keyed by our canonical WindowKey (the payload keys it by
// raw string; we narrow here).
export type WindowCoverageMap = Partial<Record<WindowKey, WindowCoverageCell>>;

// Read `payload.window_coverage` tolerantly: absent / non-object / array -> {}.
// Only entries that (a) key a real WindowKey and (b) carry sparse:true plus
// numeric trading/calendar days survive, so a malformed cell is dropped rather
// than badged. Never throws; the dense-token path returns {} unchanged.
const readWindowCoverage = (raw: unknown): WindowCoverageMap => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const src = raw as Record<string, unknown>;
  const out: WindowCoverageMap = {};
  for (const key of WINDOW_KEYS) {
    const cell = src[key];
    if (!cell || typeof cell !== 'object') continue;
    const c = cell as { sparse?: unknown; trading_days?: unknown; calendar_days?: unknown };
    if (c.sparse === true && typeof c.trading_days === 'number' && typeof c.calendar_days === 'number') {
      out[key] = { sparse: true, trading_days: c.trading_days, calendar_days: c.calendar_days };
    }
  }
  return out;
};

// Normalise a raw feed payload into a WindowBundle, tolerant of two shapes:
//   1. Bundled (current contract): a `windows` map + `available_windows` +
//      `default_window`. Enable only windows both listed AND present in the map.
//   2. Legacy single-envelope: no `windows` map — treat the whole payload as the
//      24h envelope and enable 24h only. So an old file never breaks the page.
export const resolveWindowBundle = (payload: FlowPayload | null): WindowBundle => {
  const p = payload as
    | (FlowPayload & {
        windows?: Record<string, FlowPayload>;
        available_windows?: unknown;
        default_window?: unknown;
      })
    | null;

  // Coverage is a sibling of `available_windows` at the cell, present on both
  // shapes; read it once, tolerantly (absent -> {}).
  const windowCoverage = readWindowCoverage(p?.window_coverage);

  const windows = p?.windows as Partial<Record<WindowKey, FlowPayload>> | undefined;
  if (windows && typeof windows === 'object' && !Array.isArray(windows)) {
    const listed = Array.isArray(p?.available_windows) ? p.available_windows : [];
    // Windows present in the map (canonical order) — the ground truth for what
    // we can actually render.
    const present = WINDOW_KEYS.filter((k) => windows[k] != null);
    // Prefer the listed-AND-present intersection; if the feed omitted
    // `available_windows`, derive straight from the map keys (never assume 24h
    // exists — a `{ '7d': … }`-only bundle must resolve to ['7d'], not ['24h']).
    const availableWindows =
      listed.length > 0 ? present.filter((k) => listed.includes(k)) : present;
    const resolved: WindowKey[] = availableWindows.length > 0 ? availableWindows : ['24h'];
    const dw = p?.default_window;
    const defaultWindow = isWindowKey(dw) && resolved.includes(dw) ? dw : resolved[0];
    return { availableWindows: resolved, defaultWindow, windows, windowCoverage };
  }

  // Legacy single-envelope fallback.
  return {
    availableWindows: ['24h'],
    defaultWindow: '24h',
    windows: payload ? { '24h': payload } : {},
    windowCoverage,
  };
};

// Global venue/token selectors. The feed nests everything under
// `data[venue][token]` with a `venues[]` manifest — switching venue OR token is
// CLIENT-SIDE re-indexing, identical to the window switch (no re-request).

// Venue-specific settlement hour UTC (Deribit 08:00). The active venue's
// `settlement_hour_utc` overrides; declared here so the back-compat synthesis
// below can reference it.
export const DEFAULT_SETTLEMENT_HOUR = 8;

// The active token's manifest entry (or undefined if the token isn't listed).
export const findToken = (list: TokenInfo[], token: string): TokenInfo | undefined =>
  list.find((t) => t.token === token);

// The display label for a token — the manifest's `display`, else the ticker.
export const tokenLabel = (t: TokenInfo): string => t.display ?? t.token;

// The display label for a venue — the manifest's `display`, else the id.
export const venueLabel = (v: VenueInfo): string => v.display ?? v.venue;

// Message for the Structures slot on a venue that doesn't tag multi-leg
// structures (structures_supported: false, e.g. Binance/OKX). Rendered IN-PLACE
// as the 4th slot of the Flow composition panel (IM-1945) — no standalone card.
// Single source of truth for the text so slot + any future surface stay in sync.
export const structuresNotTaggedMessage = (venueLabel: string): string =>
  `Not tagged on ${venueLabel}. Multi-leg structure detection isn't available for this venue's flow yet.`;

// The active venue's entry (or undefined if not listed).
export const findVenue = (list: VenueInfo[], venue: string): VenueInfo | undefined =>
  list.find((v) => v.venue === venue);

// Consistent venue identity colours, shared by the cross-venue stacked bar, its
// legend, and the summary table (and later the venue selector). Canonical order
// stacks the bar bottom-up. Unknown venues fall back to a neutral hue.
export const VENUE_ORDER = ['deribit', 'okx', 'binance', 'bybit', 'bullish'] as const;

const VENUE_COLOR_TOKENS: Record<string, string> = {
  deribit: 'charts.lightBlue',
  okx: 'charts.purple',
  binance: 'charts.yellow',
  bybit: 'charts.orange',
  bullish: 'charts.turquoise',
};
const VENUE_COLOR_FALLBACK = 'charts.pink';

// The tokens.* path (CSS-var string for sx / getTokenValue for SVG hex).
export const venueColorToken = (venue: string): string =>
  VENUE_COLOR_TOKENS[venue] ?? VENUE_COLOR_FALLBACK;

// Order a venue set by the canonical VENUE_ORDER, appending any unknown venues
// (alphabetical) so a new venue still renders deterministically.
export const orderVenues = (venues: string[]): string[] => {
  const known = VENUE_ORDER.filter((v) => venues.includes(v));
  const extra = venues
    .filter((v) => !VENUE_ORDER.includes(v as (typeof VENUE_ORDER)[number]))
    .sort();
  return [...known, ...extra];
};

// The normalised nested feed: the venue manifest + the default venue + the
// venue->token->window-bundle map. Both the venue and token selectors and the
// active-envelope lookup read off this.
export interface VenueBundle {
  venues: VenueInfo[];
  defaultVenue: string;
  data: Record<string, Record<string, FlowPayload>>;
}

const BACKCOMPAT_VENUE = 'deribit';
const BACKCOMPAT_TOKEN = 'BTC';

// Normalise a raw feed payload into a VenueBundle, tolerant of two shapes:
//   1. Nested (current contract): `venues[]` + `default_venue` + `data`.
//   2. Legacy (pre-restructure): no `data`/`venues` — treat the WHOLE payload as
//      deribit/BTC's window-bundle so nothing breaks before the feed nests. The
//      synthesised venue offers a single BTC token (all the data carries).
export const resolveVenueBundle = (payload: FlowPayload | null): VenueBundle => {
  const venues = payload?.venues;
  const data = payload?.data;
  if (Array.isArray(venues) && venues.length > 0 && data && typeof data === 'object') {
    const valid = venues.filter(
      (v): v is VenueInfo => !!v && typeof v === 'object' && typeof v.venue === 'string',
    );
    const dv = payload?.default_venue;
    const defaultVenue =
      typeof dv === 'string' && valid.some((v) => v.venue === dv)
        ? dv
        : (valid.find((v) => v.default)?.venue ?? valid[0]?.venue ?? BACKCOMPAT_VENUE);
    return { venues: valid, defaultVenue, data };
  }

  // Legacy fallback: the whole payload IS deribit/BTC's window-bundle.
  const btc: TokenInfo = { token: BACKCOMPAT_TOKEN, token_pair: 'BTC_USD', liquidity: 'high' };
  return {
    venues: [
      {
        venue: BACKCOMPAT_VENUE,
        display: 'Deribit',
        default: true,
        default_token: BACKCOMPAT_TOKEN,
        tokens: [btc],
        structures_supported: true,
        blocks_supported: true,
        settlement_hour_utc: DEFAULT_SETTLEMENT_HOUR,
      },
    ],
    defaultVenue: BACKCOMPAT_VENUE,
    data: payload ? { [BACKCOMPAT_VENUE]: { [BACKCOMPAT_TOKEN]: payload } } : {},
  };
};

// Cross-venue comparison lives TOP-LEVEL (sibling to data/venues), not in a
// window envelope. Returns it only when it carries content (a windowed map OR a
// flat back-compat series), so the panel self-hides on today's single-venue feed.
export const resolveVenueComparison = (
  payload: FlowPayload | null,
): VenueComparisonReport | null => {
  const vc = payload?.venue_comparison;
  if (!vc) return null;
  const hasWindows =
    !!vc.windows && typeof vc.windows === 'object' && Object.keys(vc.windows).length > 0;
  const hasFlat = Array.isArray(vc.series) && vc.series.length > 0;
  return hasWindows || hasFlat ? vc : null;
};

// The active window's slice of the comparison, following the SAME global
// activeWindow that drives the per-venue view. Tolerant of two shapes:
//   - Windowed: index windows[activeWindow]; if the report lacks that window,
//     fall back to the first available one (panel shows what it has).
//   - Flat (back-compat): the top-level series/summary IS the current window.
// Returns null when there's nothing to show.
export const resolveVenueComparisonWindow = (
  report: VenueComparisonReport | null,
  activeWindow: string,
): VenueComparisonWindow | null => {
  if (!report) return null;

  const windows = report.windows;
  if (windows && typeof windows === 'object') {
    const present = (report.available_windows ?? Object.keys(windows)).filter(
      (w) => windows[w] != null,
    );
    // Prefer the active window; else the report's default_window; else the first
    // available. (All 6 windows exist on the cross-venue report, so the active
    // one is normally present; this is graceful fallback.)
    const fallback =
      report.default_window && windows[report.default_window] ? report.default_window : present[0];
    const key = windows[activeWindow] ? activeWindow : fallback;
    const w = key ? windows[key] : undefined;
    if (!w) return null;
    return {
      series: w.series ?? [],
      summary: w.summary ?? [],
      granularity: w.granularity,
      meta: w.meta,
    };
  }

  if (Array.isArray(report.series)) {
    return {
      series: report.series,
      summary: report.summary ?? [],
      granularity: report.meta?.granularity,
      meta: { window_from: report.meta?.window_from, window_to: report.meta?.window_to },
    };
  }
  return null;
};

// Canonical flow-metric ordering — Notional · Premium · Trades — the SINGLE
// source of truth every metric list/toggle/column-triad in the flow UI derives
// from (client-feedback IM-1934). Components map each id to their own field
// key / label / formatter; only the ORDER lives here, so reordering is one edit
// and every site stays consistent.
export type FlowMetric = 'notional' | 'premium' | 'trades';
export const METRIC_ORDER: readonly FlowMetric[] = ['notional', 'premium', 'trades'];
export const METRIC_LABELS: Record<FlowMetric, string> = {
  notional: 'Notional',
  premium: 'Premium',
  trades: 'Trades',
};

// The stacked-bar measure toggle. `field` reads the per-venue day stats; `usd`
// picks the formatter (notional + premium are USD, trades a count). Order comes
// from METRIC_ORDER so it tracks the canonical triad.
export type ComparisonMeasure = FlowMetric;
const COMPARISON_MEASURE_META: Record<FlowMetric, { field: keyof VenueDayStats; usd: boolean }> = {
  notional: { field: 'notional_usd', usd: true },
  premium: { field: 'premium_usd', usd: true },
  trades: { field: 'trades', usd: false },
};
export const COMPARISON_MEASURES: {
  key: ComparisonMeasure;
  label: string;
  field: keyof VenueDayStats;
  usd: boolean;
}[] = METRIC_ORDER.map((key) => ({
  key,
  label: METRIC_LABELS[key],
  ...COMPARISON_MEASURE_META[key],
}));

// An expiry is live only until it settles at the venue's settlement hour UTC on
// its expiry date — so today's expiry drops the moment it settles, not at end of
// day. (FE backstop; flow_claude also filters data-side.)
export const isExpiryLive = (
  expiryDate: string,
  now: number = Date.now(),
  settlementHour: number = DEFAULT_SETTLEMENT_HOUR,
): boolean => {
  const hh = String(settlementHour).padStart(2, '0');
  const settleMs = Date.parse(`${expiryDate}T${hh}:00:00Z`);
  return Number.isNaN(settleMs) ? true : now < settleMs;
};

// Live (not-yet-settled) expiries, chronological. Shared by the chain expiry
// strip and the ranked expiry buttons so both filter identically. Falls back to
// all (sorted) if nothing is live, so a selector never blanks.
export const futureExpiries = <T extends { expiry_date: string }>(
  items: T[],
  now: number = Date.now(),
  settlementHour: number = DEFAULT_SETTLEMENT_HOUR,
): T[] => {
  const sorted = [...items].sort((a, b) => a.expiry_date.localeCompare(b.expiry_date));
  const live = sorted.filter((e) => isExpiryLive(e.expiry_date, now, settlementHour));
  return live.length > 0 ? live : sorted;
};

// The largest absolute value in a set — the shared scale for a column of
// DivergingBars. Returns 0 for an empty set; DivergingBar's own safeMax guard
// then floors the divisor, so callers don't repeat an epsilon.
export const maxAbs = (values: number[]): number =>
  values.length === 0 ? 0 : Math.max(...values.map((v) => Math.abs(v)));

// A signed number to `dp` places with an explicit leading + (for gap/z-score
// metrics where the sign carries meaning).
const fmtSigned = (value: number, dp: number): string => {
  const n = Number(value.toFixed(dp));
  return n > 0 ? `+${n}` : String(n);
};

// The ranked headline column, keyed off `report.primary_metric`. This retires
// the old `trades`-presence heuristic: most_active, unusual_activity
// (vol_zscore), iv_vs_mark (iv_gap_volpts) and top_flow (premium) all resolve
// their correct label + formatter here, and an unknown/absent metric falls back
// to premium — so a future metric renders (as premium) rather than breaking.
export interface RankedMetric {
  key: string;
  label: string;
  format: (value: number | null | undefined) => string;
}

export const rankedMetric = (primaryMetric?: string): RankedMetric => {
  switch (primaryMetric) {
    case 'trades':
      return { key: 'trades', label: 'Trades', format: fmtCount };
    case 'vol_zscore':
      return {
        key: 'vol_zscore',
        label: 'Vol z-score',
        format: (v) => (v == null || Number.isNaN(v) ? '—' : fmtSigned(v, 1)),
      };
    case 'iv_gap_volpts':
      return {
        key: 'iv_gap_volpts',
        label: 'IV gap',
        format: (v) => (v == null || Number.isNaN(v) ? '—' : fmtSigned(v, 1)),
      };
    case 'premium_usd':
    default:
      return { key: 'premium_usd', label: 'Premium', format: fmtUsd };
  }
};

// Windowing math for a fixed-row-height scroll list (the ranked ALL view, ~1000
// instruments): given the scroll offset + viewport, return the slice to render
// plus the spacer heights that preserve the full scrollbar. Pure + clamped, so
// it's unit-testable without a DOM.
export interface WindowSlice {
  start: number;
  end: number; // exclusive
  padTop: number;
  padBottom: number;
}

export const computeWindow = (
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  count: number,
  overscan = 4,
): WindowSlice => {
  if (rowHeight <= 0 || count <= 0) return { start: 0, end: 0, padTop: 0, padBottom: 0 };
  const first = Math.floor(Math.max(0, scrollTop) / rowHeight);
  const visible = Math.ceil(viewportHeight / rowHeight);
  const start = Math.max(0, first - overscan);
  const end = Math.min(count, first + visible + overscan);
  return {
    start,
    end,
    padTop: start * rowHeight,
    padBottom: Math.max(0, (count - end) * rowHeight),
  };
};

// Format a timeseries value by its report's `y_format`, so the axis + tooltip
// format correctly for any timeseries report (current + Tier-1) with no code
// change. Unknown formats fall back to USD (the current marquee unit).
export const formatFlowValue = (value: number, yFormat: string): string => {
  if (value == null || Number.isNaN(value)) return '—';
  switch (yFormat) {
    case 'count':
      return fmtCount(value);
    case 'ratio':
      return String(Number(value.toFixed(3))); // x.xxx, trailing zeros stripped
    case 'pct':
      return `${Number(value.toFixed(1))}%`; // NN.N%
    case 'index':
      return String(Number(value.toFixed(2))); // plain number (symmetric axis)
    case 'usd':
    default:
      return fmtUsd(value);
  }
};

// ISO-8601 UTC -> lightweight-charts UTCTimestamp (epoch SECONDS). Returns null
// for an unparseable stamp so callers can drop the point rather than plot NaN.
export const isoToChartTime = (iso: string): number | null => {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
};
